/**
 * Implantación — detalle (implantacion.html): la memoria completa de la
 * casa (ADR-6) y el INSPECTOR «no es magia». REAL: nombre, sector, sabor y
 * recuento de directivos (§5: metadatos, nunca nombres de personas);
 * criterios, pendientes, decisiones, seguimientos y episodios desde la API.
 * Los Episodios son las CONVERSACIONES y se navegan: el seleccionado se abre
 * entero arriba, turno a turno, y una respuesta a la vez enseña su TRAZA,
 * que viaja con el turno (`turn.trace`) — un turno anterior a la traza lo
 * dice, no se la inventa. La selección vive en la URL (`?ep=&turno=`) para
 * poder enlazar un mensaje concreto. Con episodios (ADR-21; M5 paso 3b,
 * 2026-11-03), cada uno dice si está abierto, cerrado o es anterior a los
 * episodios con puente; el inspector despliega su puente (texto, modelo,
 * tokens) y la traza de la respuesta que lo recibió lo cita. En «Sus
 * fuentes», los documentos de cifras de la casa como METADATOS (ADR-22
 * pt 8: fijados, borrador, conceptos, último corte). SEMILLA: solo fuentes, telemetría y
 * rastro (F5c), siempre marcados «fase dev». Banner §5 SIEMPRE:
 * instrumento de fase dev — se gatea antes de clientes reales.
 */
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useQueries, useQuery } from "@tanstack/react-query";
import type { EpisodeSummary, Memoria, Turn as Mensaje } from "@skein/contracts";
import { Badge, Banner, BarRow, Bubble, Button, Card, CardHeader, CardPad, Conn, Delta, Disclose, Dl, Empty, Fainted, Feed, FeedItem, FeedWho, MachineTag, MemList, Mono, Opaque, PageHead, Prose, RailTitle, Skeleton, Stale, StatCell, StatGrid, Tab, Tabs, TraceRow, Turn, ProseBlocks, useToast } from "@skein/ui";
import { useState } from "react";
import { studio } from "../../api/client";
import { retryEvery30s, useEngagements } from "../../api/queries";
import { idCorto, lineaDocumentosCifras, nombreCasa, recuentoDirectivos, saborCasa } from "./casa";
import { casaDe, esSembrada } from "./casas.seed";
import { ROTULO_ESTADO, costeEpisodio, cuentaTurnos, documentoDeTraza, episodioAbierto, episodiosAPedir, estadoEpisodio, formatCoste, formatTokens, nacioDe, porRecencia, puenteDeTraza, recuerdoDeTraza, recuerdosEpisodio, titularEpisodio, tokensPuente, turnoAbierto, type DocumentoDeTraza, type ImplantacionSearch, type RecuerdoDeTraza } from "./episodios";
import { estadoDe } from "./estado";
import { diasNaturales, fechaCorta as fCorta, fechaLarga as fLarga, fechaTabla } from "./fecha";

/** §5: el equipo ve metadatos, no la identidad del directivo. Ni de la API
 *  ni de la semilla sale un nombre — se le llama por su papel. */
const QUIEN = "su directivo/a";
/** Extracto presentable: corte por palabra, elipsis solo si hace falta, sin manosear mayúsculas. */
const extracto = (t: string, max = 40) => (t.length <= max ? t : `${t.slice(0, max).replace(/\s+\S*$/, "")}…`);
/** Lo que la página necesita de una consulta de turnos (la de TanStack encaja). */
interface ConsultaTurnos { data?: Mensaje[] | undefined; isPending: boolean; isError: boolean; refetch: () => unknown }

function Cargando() {
  const ln = (w: string, extra?: React.CSSProperties) => <Skeleton style={{ height: ".9em", width: w, ...extra }} />;
  const gap = { marginBottom: "var(--sk-sp-3)" };
  return (
    <section aria-busy="true" aria-label="Cargando la implantación">
      <PageHead title="" skeleton />
      <div className="cols">
        <div className="stack">
          <Card>{ln("50%", gap)}{ln("100%")}{ln("85%", { marginTop: "var(--sk-sp-2)" })}{ln("70%", { marginTop: "var(--sk-sp-2)" })}</Card>
          <Card>{ln("40%", gap)}{ln("100%")}{ln("60%", { marginTop: "var(--sk-sp-2)" })}</Card>
          <Card>{ln("45%", gap)}<Skeleton style={{ height: "4rem" }} /></Card>
        </div>
        <div className="stack">
          <Card>{ln("55%", gap)}<Skeleton style={{ height: "2rem" }} /></Card>
          <Card>{ln("50%", gap)}<Skeleton style={{ height: "3.5rem" }} /></Card>
          <Card>{ln("45%", gap)}{ln("100%")}{ln("75%", { marginTop: "var(--sk-sp-2)" })}</Card>
        </div>
      </div>
    </section>
  );
}

/** La traza de UNA respuesta: lo que verá auditoría. El acto que la abre la
 *  gobierna (`aria-controls`) y puede devolverle el foco, así que la región
 *  tiene nombre, id y es enfocable por programa (nunca con Tab). */
function Traza({ id, turno, criterios, episodios, onEpisodio }: {
  id: string; turno: Mensaje; criterios: readonly { statement: string }[];
  episodios: readonly EpisodeSummary[]; onEpisodio: (conversationId: string) => void;
}) {
  const trace = turno.trace ?? null;
  // el puente que viajó a ESTA respuesta (ADR-21 pt 2): de qué conversación y su fecha
  const puente = puenteDeTraza(trace, episodios);
  // el recuerdo bajo demanda de ESTA respuesta (ADR-21 pt 5): consulta, rango y fragmentos por referencia
  const recuerdo = recuerdoDeTraza(trace, episodios);
  // la entrada por documento de ESTA respuesta (ADR-21 pt 6): nombre, candidatos y correcciones
  const documento = documentoDeTraza(trace);
  return (
    <div id={id} tabIndex={-1} aria-label="La traza de esta respuesta">
      <RailTitle sub="· lo que verá auditoría">La traza de esta respuesta</RailTitle>
      {trace ? (
        <>
          <div style={{ marginBottom: "var(--sk-sp-2)" }}>
            {trace.skillSlug
              ? <TraceRow win slug={trace.skillSlug} {...(trace.skillTitle ? { titulo: trace.skillTitle } : {})} />
              : <Fainted block>ningún método se disparó — la respuesta salió de la base del asesor, no de un skill</Fainted>}
          </div>
          <Dl style={{ margin: "var(--sk-sp-4) 0" }} items={[
            // el extracto solo se enseña si la memoria de hoy es la que recibió
            // el asesor: citar un criterio que llegó DESPUÉS sería mentir
            { dt: "criterios recibidos", dd: <>{trace.confirmedFacts} confirmados{trace.confirmedFacts === criterios.length && criterios.length > 0 && <> — entre ellos <b>{extracto(criterios[0]!.statement)}</b></>}</> },
            { dt: "decisiones conocidas", dd: <>{trace.decisionsKnown}{trace.decisionsKnown > 0 && <> — la última condiciona la recomendación</>}</> },
            { dt: "seguimientos abiertos", dd: `${trace.openFollowUps}` },
            { dt: "historia", dd: <>{trace.historyTurns} turnos tenidos en cuenta — el recall nunca se fabrica (ADR-2)</> },
            // lo que esta respuesta consumió (ADR-18 pt 7, revisión 2026-10-19):
            // tokens tal como se guardaron; el coste, con el precio de HOY
            { dt: "modelo", dd: trace.model
              ? <><Mono>{trace.model.name}</Mono> · {trace.model.calls} {trace.model.calls === 1 ? "llamada" : "llamadas"} · {formatTokens(trace.model.inputTokens)} de entrada{trace.model.cacheReadTokens > 0 && <> ({formatTokens(trace.model.cacheReadTokens)} de caché)</>} · {formatTokens(trace.model.outputTokens)} de salida</>
              : <Fainted>sin consumo guardado — modelo falso, o turno anterior a la traza del consumo</Fainted> },
            // sin consumo, la fila de arriba ya lo explica: la de coste se omite
            ...(trace.model ? [{ dt: "coste", dd: trace.model.cost
              ? <><b>{formatCoste(trace.model.cost.amount, trace.model.cost.currency)}</b> — al precio de hoy, no al del día de la respuesta</>
              : <Fainted>sin precio para este modelo — configurar <Mono>MODEL_PRICES</Mono> en el servidor</Fainted> }] : []),
            // modo personas (ADR-17 pt 5): la respuesta nombra a alguien por su alias — solo la marca, nunca el alias
            ...(trace.people ? [{ dt: "personas", dd: "nombra a una persona por su alias — casa con modo personas: lectura de pruebas, sin decisiones sobre nadie" }] : []),
            // el puente (ADR-21 pt 2): la traza lo guarda por referencia, no su texto; el texto está en su episodio
            ...(puente ? [{ dt: "puente", dd: <>Puente del {fLarga(puente.fecha)} — retomó el hilo de esa conversación{puente.episodio && <> · <Button variant="link" onClick={() => onEpisodio(puente.episodio!.conversationId)}>abrir ese episodio</Button></>}</> }] : []),
            // el recuerdo bajo demanda (ADR-21 pt 5): como el puente, por referencia; el texto está en su episodio
            ...(recuerdo ? [{ dt: "recuerdo", dd: <FilaRecuerdo recuerdo={recuerdo} onEpisodio={onEpisodio} /> }] : []),
            // la entrada por documento (ADR-21 pt 6): como el puente y el recuerdo, por referencia; los hechos están en la memoria
            ...(documento ? [{ dt: "documento", dd: <FilaDocumento documento={documento} /> }] : []),
            // la traza NO guarda fuentes y la conexión de la semilla es un
            // maniquí: en el bloque de auditoría no entra nada sembrado
            { dt: "datos", dd: "sin fuente conectada — el asesor razona con criterio, no con cálculo (R-4)" },
          ]} />
        </>
      ) : (
        <Fainted block style={{ margin: "var(--sk-sp-2) 0 var(--sk-sp-4)" }}>este turno es anterior a la traza: no se guardó qué recibió el asesor y no se inventa. Los turnos nuevos la traen.</Fainted>
      )}
      <div style={{ display: "flex", gap: "var(--sk-sp-2)" }}>
        <MachineTag kind="ia">IA</MachineTag>
        <MachineTag kind="det">enrutado determinista</MachineTag>
        <MachineTag kind="gate">puerta del sí</MachineTag>
      </div>
    </div>
  );
}

/** La fila «documento» de la traza (ADR-21 pt 6): qué documento, cuántos hechos dejó por confirmar y cuántos corrigen uno confirmado. */
function FilaDocumento({ documento }: { documento: DocumentoDeTraza }) {
  if (documento.estado === "fallo") return <Fainted>«{documento.nombre}» — la lectura del documento falló o se pasó de tiempo: no propuso nada y lo dijo</Fainted>;
  const { nombre, candidatos, correcciones, fuera } = documento;
  return (
    <>
      «{nombre}» — {candidatos === 0 ? "ningún hecho nuevo" : <>{candidatos} {candidatos === 1 ? "hecho" : "hechos"} por confirmar en bloque</>}
      {correcciones > 0 && <>, {correcciones} {correcciones === 1 ? "corrige" : "corrigen"} uno confirmado</>}
      {fuera && <> · <Fainted>fuera: {fuera}</Fainted></>}
    </>
  );
}

/** La fila «recuerdo» de la traza (ADR-21 pt 5): lo que se buscó y lo que volvió, cada fragmento con su episodio. */
function FilaRecuerdo({ recuerdo, onEpisodio }: { recuerdo: RecuerdoDeTraza; onEpisodio: (conversationId: string) => void }) {
  if (recuerdo.estado === "no-pedido") return <Fainted>miraba al pasado, pero no pedía recordar una conversación — no se buscó</Fainted>;
  if (recuerdo.estado === "fallo") return <Fainted>{recuerdo.buscado ? "la búsqueda en lo hablado falló" : "el paso de recuerdo falló o se pasó de tiempo"} — respondió sin anexo y lo dijo</Fainted>;
  // los días del rango son días de la casa (AAAA-MM-DD): a mediodía, para que ninguna zona horaria los mueva
  const dia = (d: string) => fCorta(`${d}T12:00:00`);
  const rango = recuerdo.desde && recuerdo.hasta ? <> · del {dia(recuerdo.desde)} al {dia(recuerdo.hasta)}</> : recuerdo.desde ? <> · desde el {dia(recuerdo.desde)}</> : recuerdo.hasta ? <> · hasta el {dia(recuerdo.hasta)}</> : null;
  const consulta = recuerdo.consulta ? <>«{recuerdo.consulta}»</> : <>solo por fechas</>;
  if (recuerdo.estado === "vacio") return <>{consulta}{rango} — pidió recordar y no se encontró nada (cuenta para OQ-20)</>;
  return (
    <>
      {consulta}{rango} — {recuerdo.fragmentos.length} {recuerdo.fragmentos.length === 1 ? "fragmento" : "fragmentos"}:
      {recuerdo.fragmentos.map((f, j) => (
        <span key={j}>{j > 0 ? ";" : ""} del {fLarga(f.fecha)} ({f.autor}){f.episodio && <> · <Button variant="link" onClick={() => onEpisodio(f.conversationId)}>abrir ese episodio</Button></>}</span>
      ))}
    </>
  );
}

/**
 * El episodio y su PUENTE (ADR-21; M5 paso 3b): estado, tramo y, si está
 * cerrado y lo tiene, el puente desplegable — su texto entero, el modelo que
 * lo escribió y sus tokens (sin precio: el coste del inspector sale de la
 * traza de un turno y el puente no la tiene). Lo que falta se dice: abierto,
 * se escribe al cerrarse; cerrado sin puente, el porqué posible; anterior a
 * los episodios, no lo tiene ni lo tendrá. Una API anterior no dice nada:
 * nada se pinta.
 */
function EpisodioYPuente({ episodio }: { episodio: EpisodeSummary }) {
  const estado = estadoEpisodio(episodio);
  if (estado === null) return null;
  const p = episodio.puente ?? null;
  const tokens = p ? tokensPuente(p) : null;
  return (
    <Dl style={{ margin: "0 0 var(--sk-sp-4)" }} items={[
      { dt: "estado", dd: estado === "abierto"
        ? <><b>{ROTULO_ESTADO.abierto}</b> · desde {fechaTabla(episodio.desde, false)} · último mensaje {fechaTabla(episodio.hasta, false)}</>
        : <><b>{ROTULO_ESTADO[estado]}</b> · del {fCorta(episodio.desde)} al {fLarga(episodio.hasta)}</> },
      { dt: "puente", dd: p
        ? <Disclose act={`ver el puente del ${fLarga(p.fecha)}`}>
            <ProseBlocks texto={p.texto} parrafo={(c, j) => <Prose key={j} style={{ margin: "var(--sk-sp-2) 0" }}>{c}</Prose>} />
            <Fainted block>{p.modelo ? <Mono>{p.modelo}</Mono> : "modelo sin guardar"}{tokens && <> · {tokens}</>} · escrito {fechaTabla(p.escritoEn, false)}</Fainted>
          </Disclose>
        : <Fainted>{estado === "abierto"
          ? "se escribe al cerrarse la conversación"
          : estado === "legado"
            ? "ninguno — esta conversación es anterior a los episodios con puente"
            : "ninguno — su directivo/a no habló en ella, o aún no se ha escrito (el barrido lo reintenta)"}</Fainted> },
    ]} />
  );
}

/** Un id por turno: el acto y su región de traza se apuntan el uno al otro. */
const trazaId = (i: number) => `traza-turno-${i}`;
/** Devolver el foco a la traza ya abierta (y traerla a la vista). */
function irALaTraza(id: string) {
  const el = document.getElementById(id);
  el?.scrollIntoView({ block: "nearest" });
  el?.focus();
}

/**
 * El episodio ENTERO, en orden, con una sola respuesta inspeccionada: la
 * traza es de UN turno, no del episodio, y enseñar cinco a la vez sería
 * volver a no saber qué recibió el asesor para responder ESTE mensaje.
 */
function Inspector({ episodio, turnos, sel, onTurno, criterios, episodios, onEpisodio }: {
  episodio: EpisodeSummary | undefined;
  turnos: ConsultaTurnos | undefined;
  sel: number | undefined;
  onTurno: (i: number) => void;
  criterios: readonly { statement: string }[];
  episodios: readonly EpisodeSummary[];
  onEpisodio: (conversationId: string) => void;
}) {
  if (!episodio || !turnos) return <CardPad><Empty inline title="Aún no hay episodios.">El inspector se enciende con la primera conversación de la casa.</Empty></CardPad>;
  // el inspector es la pieza central: su error se cuenta, no se queda en blanco
  if (turnos.isError) return (
    <CardPad>
      <Banner tone="danger"><b>No llego al episodio.</b> El inspector no puede enseñarte qué recibió el asesor. Reintento solo cada 30 s. <Button variant="link" onClick={() => void turnos.refetch()}>reintentar ahora</Button></Banner>
    </CardPad>
  );
  if (turnos.isPending) return <CardPad><Skeleton style={{ height: "4rem" }} /></CardPad>;
  const lista = turnos.data ?? [];
  if (lista.length === 0) return (
    <CardPad><Empty inline title="Este episodio no tiene turnos que inspeccionar.">Se abrió, pero no llegó a hablarse — nada que enseñar aquí.</Empty></CardPad>
  );
  const coste = costeEpisodio(lista);
  // el contador de OQ-20 en este episodio: cuántas veces pidió recordar y cuántas sin fragmento
  const recuerdos = recuerdosEpisodio(lista);
  return (
    <CardPad>
      <EpisodioYPuente episodio={episodio} />
      {/* lo que el episodio lleva gastado: la suma de las respuestas con
          coste, al precio de hoy; las que no lo traen se dicen, no se
          cuentan como cero */}
      <Fainted block style={{ marginBottom: "var(--sk-sp-4)" }}>
        {coste
          ? <>Este episodio lleva <b>{formatCoste(coste.amount, coste.currency)}</b> en {coste.valoradas} {coste.valoradas === 1 ? "respuesta" : "respuestas"}{coste.sinValorar > 0 && <> ({coste.sinValorar} sin valorar)</>} — al precio de hoy.</>
          : <>Ninguna respuesta de este episodio trae su coste: anteriores a la traza del consumo, o del modelo falso.</>}
      </Fainted>
      {recuerdos && (
        <Fainted block style={{ marginBottom: "var(--sk-sp-4)" }}>
          Pidió recordar una conversación anterior <b>{recuerdos.pedidos}</b> {recuerdos.pedidos === 1 ? "vez" : "veces"}; {recuerdos.sinFragmento === 0 ? "siempre se encontró algo" : <><b>{recuerdos.sinFragmento}</b> sin fragmento — el contador de OQ-20</>}.
        </Fainted>
      )}
      {lista.map((t, i) => t.role === "user" ? (
        <Turn key={i} me name="Su directivo/a" tm={fechaTabla(t.at, false)}>
          <Bubble>{t.content}</Bubble>
        </Turn>
      ) : (
        <Turn key={i} name="Su asesor" tm={fechaTabla(t.at, false)}>
          {/* como lo leyó el directivo: por párrafos, no un muro de diez
              líneas, y la tabla con la misma pieza que en el Despacho */}
          <ProseBlocks texto={t.content} parrafo={(c, j) => <Prose key={j} style={{ marginBottom: "var(--sk-sp-3)" }}>{c}</Prose>} />
          {/* el acto gobierna la región de la traza (aria-expanded, y
              aria-controls solo cuando la región existe: apuntar a un id
              ausente sería una referencia rota) y NUNCA es inerte: con la
              traza abierta, le lleva el foco — así el teclado alcanza lo que
              el ratón ve de un vistazo */}
          <Button
            variant="link" aria-expanded={i === sel}
            {...(i === sel ? { "aria-controls": trazaId(i) } : {})}
            onClick={() => (i === sel ? irALaTraza(trazaId(i)) : onTurno(i))}
            style={{ marginBottom: "var(--sk-sp-4)" }}
          >
            {i === sel ? "ir a la traza" : "ver la traza"}
          </Button>
          {i === sel && <Traza id={trazaId(i)} turno={t} criterios={criterios} episodios={episodios} onEpisodio={onEpisodio} />}
        </Turn>
      ))}
    </CardPad>
  );
}

export function ImplantacionPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const search = useSearch({ strict: false }) as ImplantacionSearch;
  const navigate = useNavigate();
  const say = useToast();
  const memoria = useQuery({ queryKey: ["memoria", id], queryFn: () => studio.memoria(id), ...retryEvery30s });
  const engagements = useEngagements();
  const [tab, setTab] = useState<"criterios" | "pendientes" | "decisiones" | "seguimientos">("criterios");

  // Los episodios del más reciente al más viejo, y los turnos de los que el
  // feed cita: el titular es el arranque REAL del primer mensaje del
  // directivo (la API no guarda título de episodio y no se inventa). Con
  // tope (episodiosAPedir): los recientes se citan, los viejos se quedan con
  // su fecha y su recuento — y de paso, cambiar de episodio es instantáneo.
  const episodios = porRecencia(memoria.data?.episodios ?? []);
  const abierto = episodioAbierto(episodios, search.ep);
  const pedidos = episodiosAPedir(episodios, abierto);
  const turnos = useQueries({
    queries: pedidos.map((e) => ({ queryKey: ["episodio", id, e.conversationId], queryFn: () => studio.episodio(id, e.conversationId), ...retryEvery30s })),
  });
  const porConversacion = new Map(pedidos.map((e, i) => [e.conversationId, turnos[i]]));
  const consulta = abierto ? porConversacion.get(abierto.conversationId) : undefined;
  const sel = turnoAbierto(consulta?.data ?? [], search.turno);
  // la selección es estado de VISTA, no navegación: se reemplaza la entrada
  // para que «atrás» vuelva a la lista de casas, no a la respuesta anterior
  const irA = (next: { ep: string; turno?: number }) => void navigate({
    to: "/implantaciones/$id", params: { id }, replace: true,
    search: (prev: ImplantacionSearch): ImplantacionSearch => ({ ...(prev.estado !== undefined ? { estado: prev.estado } : {}), ...next }),
  });

  const forced = search.estado;
  const view = forced === "cargando" || forced === "error" ? forced
    // los metadatos de la casa (§5) viajan en la lista: sin ella la cabecera
    // no sabría ni cómo se llama, así que la espera es de las dos
    : memoria.isPending || engagements.isPending ? "cargando" : memoria.isError && !memoria.data ? "error-sin-datos" : memoria.isError ? "error" : "detalle";
  if (view === "cargando") return <Cargando />;
  if (view === "error-sin-datos" || !memoria.data) return (
    <Banner tone="danger"><b>No llego a la memoria de la casa.</b> Y aún no tengo copia que enseñarte. Reintento solo cada 30 s. <Button variant="link" onClick={() => void memoria.refetch()}>reintentar ahora</Button></Banner>
  );

  const m: Memoria = memoria.data;
  const enError = view === "error";
  const seed = casaDe(id);
  const resumen = engagements.data?.find((e) => e.engagementId === id);
  const estado = resumen ? estadoDe(resumen) : undefined;
  const criterios = m.perfil.filter((c) => c.head).map((c) => c.head!);
  const pendientes = m.perfil.filter((c) => c.pending).map((c) => c.pending!);
  const abiertos = m.pendientes.filter((p) => p.state === "open");
  // la casa empieza el día del alta (createdAt, real); si aún no la tiene
  // (casas del esqueleto), lo más viejo que sabemos es su primer episodio
  const desde = resumen?.createdAt ?? [...m.episodios].sort((a, b) => (a.desde < b.desde ? -1 : 1))[0]?.desde;
  const nombre = nombreCasa(id, resumen?.companyName);
  const sub = (f: { proposedAt: string; resolvedAt?: string | undefined; resolvedBy?: string | undefined; origin?: string | undefined }, confirmado: boolean) => confirmado
    ? `desde el ${fCorta(f.resolvedAt ?? f.proposedAt)} · dijo «sí» ${QUIEN} · ${nacioDe(f.origin)}`
    : `propuesto el ${fCorta(f.proposedAt)}${f.origin === "document" ? " · salió de un documento que adjuntó" : ""} · aguarda el «sí» de ${QUIEN} — sin él, NO forma parte del criterio`;

  return (
    <>
      {enError && (
        <Banner tone="danger" style={{ marginBottom: "var(--sk-gap)" }}><b>No llego a la memoria de la casa.</b> Lo que ves puede estar desactualizado. Reintento solo cada 30 s. <Button variant="link" onClick={() => void memoria.refetch()}>reintentar ahora</Button></Banner>
      )}
      <Stale stale={enError}>
        <PageHead
          title={nombre}
          meta={<>
            {estado === "activa" && <Badge tone="ok">activa</Badge>}
            {estado === "onboarding" && <Badge tone="accent">onboarding</Badge>}
            {estado === "parada" && <Badge tone="warn">sin actividad 14d</Badge>}
            <Badge tip={id}><Mono>{idCorto(id)}</Mono></Badge>
            {resumen?.sector && <Badge>sector · {resumen.sector}</Badge>}
            {resumen?.flavor && <Badge>sabor · {saborCasa(resumen.flavor)}</Badge>}
            {resumen && <Badge>{recuentoDirectivos(resumen.executives, resumen.admins)}</Badge>}
            {desde && <Badge>{diasNaturales(desde) === 0 ? "alta hoy" : `desde el ${fLarga(desde)}`}</Badge>}
          </>}
          actions={<>
            <Button variant="danger" disabled={enError} onClick={() => say("Pausar exige su rito (consecuencias a la vista y mantener pulsado) y llega con F5b.")}>Pausar implantación</Button>
            <Button disabled={enError} onClick={() => say("El sandbox conversando como esta casa llega con F5b.")}>Probar en sandbox</Button>
          </>}
        />
        <Banner tone="warn" style={{ marginBottom: "var(--sk-gap)" }}><b>Instrumento de fase de desarrollo.</b> Este inspector muestra contenido de la memoria; el modelo (§5) concede al equipo metadatos, no contenido. Se gatea antes de clientes reales.</Banner>
        <div className="cols">
          <div className="stack">
            <Card flush aria-label="La memoria de la casa">
              <CardHeader title="La memoria" hint="todo lo que el asesor sabe de esta casa — y por qué lo sabe" />
              <Tabs style={{ padding: "0 var(--sk-sp-3)" }}>
                <Tab selected={tab === "criterios"} onClick={() => setTab("criterios")} mod={criterios.length}>Criterios</Tab>
                <Tab selected={tab === "pendientes"} onClick={() => setTab("pendientes")} mod={pendientes.length}>Pendientes</Tab>
                <Tab selected={tab === "decisiones"} onClick={() => setTab("decisiones")} mod={m.decisiones.length}>Decisiones</Tab>
                <Tab selected={tab === "seguimientos"} onClick={() => setTab("seguimientos")} mod={abiertos.length}>Seguimientos</Tab>
              </Tabs>
              <div role="tabpanel">
                {tab === "criterios" && (criterios.length
                  ? <MemList className="memlist" items={criterios.map((f) => ({ b: f.statement, sub: sub(f, true) }))} />
                  : <CardPad><Empty inline title="Sin criterios confirmados aún.">Los criterios nacen de la conversación y solo entran con el «sí» de su directivo/a (R-2).</Empty></CardPad>)}
                {tab === "pendientes" && (pendientes.length
                  ? <MemList className="memlist" items={pendientes.map((f) => ({ b: f.statement, sub: sub(f, false) }))} />
                  : <CardPad><Empty inline title="Nada aguarda confirmación.">Cuando el asesor proponga un criterio, esperará aquí el «sí».</Empty></CardPad>)}
                {tab === "decisiones" && (m.decisiones.length
                  ? <MemList className="memlist" items={m.decisiones.map((d) => ({ b: d.statement, sub: `apuntada el ${fCorta(d.at)} · actor ${QUIEN} · fuente: conversación` }))} />
                  : <CardPad><Empty inline title="Sin decisiones apuntadas.">«Si dices que queda apuntado, emite el bloque» — aún no ha pasado (ADR-6).</Empty></CardPad>)}
                {tab === "seguimientos" && (abiertos.length
                  ? <MemList className="memlist" items={abiertos.map((p) => ({ b: p.statement, sub: `abierto el ${fCorta(p.at)} · un seguimiento NUNCA se cierra solo: lo cierra ${QUIEN}` }))} />
                  : <CardPad><Empty inline title="Sin seguimientos abiertos.">Lo que quede en vigilancia vivirá aquí hasta que su directivo/a lo cierre.</Empty></CardPad>)}
              </div>
            </Card>

            <Card flush aria-label="Inspector del episodio">
              <CardHeader title="Inspector — «no es magia»" hint="qué recibió exactamente el asesor para responder ESTE mensaje" />
              <Inspector
                episodio={abierto} turnos={consulta} sel={sel} criterios={criterios} episodios={episodios}
                onTurno={(i) => abierto && irA({ ep: abierto.conversationId, turno: i })}
                onEpisodio={(ep) => irA({ ep })}
              />
            </Card>

            <Card flush aria-label="Episodios">
              <CardHeader title="Episodios" hint="las conversaciones de la casa — cada mensaje, inspeccionable" />
              <Feed>
                {episodios.length === 0 && <FeedItem t="—">Aún no hay episodios.</FeedItem>}
                {/* el episodio no tiene título guardado: en vez de inventarlo se
                    CITA el arranque del primer mensaje del directivo (instrumento
                    de fase dev, ya advertido arriba) */}
                {episodios.map((e) => {
                  const cargados = porConversacion.get(e.conversationId)?.data;
                  const titular = titularEpisodio(cargados);
                  const coste = costeEpisodio(cargados);
                  const estadoE = estadoEpisodio(e);
                  return (
                    <FeedItem key={e.conversationId} t={fechaTabla(e.hasta, false)} sel={e.conversationId === abierto?.conversationId} onClick={() => irA({ ep: e.conversationId })}>
                      {/* el conversationId ya no se pinta: la selección lo
                          pone en la barra (`?ep=`), que es de donde se copia */}
                      <b>Episodio del {fCorta(e.desde)}</b>{titular && <> · «{titular}»</>} · {cuentaTurnos(e.turnos)}{coste && <> · {formatCoste(coste.amount, coste.currency)}</>}
                      {/* ADR-21: su estado y si tiene puente (el puente se despliega en el inspector) */}
                      {estadoE !== null && <> · {ROTULO_ESTADO[estadoE]}{e.puente && <> · con puente del {fCorta(e.puente.fecha)}</>}</>}
                      {e.conversationId === abierto?.conversationId && <> · <FeedWho>abierto arriba en el inspector</FeedWho></>}
                    </FeedItem>
                  );
                })}
              </Feed>
            </Card>
          </div>

          <div className="stack">
            <Card flush aria-label="Fuentes">
              <CardHeader title="Sus fuentes" hint={esSembrada(id) ? "con el permiso de la casa · conexión simulada (fase dev)" : "con el permiso de la casa"} />
              <CardPad>
                {seed.bc
                  ? <Conn on pulse lg="BC" name="Business Central" status={seed.bcStatus ?? "conectada"} />
                  : <Conn ghost lg="BC" name="Business Central" status="cuando la casa lo necesite" />}
                {seed.pbi
                  ? <Conn on pulse lg="PBI" name="Power BI" status="al día" />
                  : <Conn ghost lg="PBI" name="Power BI" status="cuando la casa lo necesite" />}
                {/* ADR-22 pt 8: los documentos de cifras, solo metadatos (ni nombres de fichero ni cifras) */}
                {resumen && <Dl style={{ marginTop: "var(--sk-sp-3)" }} items={[{ dt: "documentos de cifras", dd: lineaDocumentosCifras(resumen.documentosCifras) }]} />}
              </CardPad>
            </Card>

            <Card flush aria-label="Telemetría de la casa">
              <CardHeader title="Actividad · 30d" hint={seed.telemetria ? "números de la fase dev — aún no medimos de verdad" : undefined} />
              {seed.telemetria ? (
                <>
                  <StatGrid>
                    <StatCell n={seed.telemetria.mensajes} label="mensajes" />
                    <StatCell n={<>{seed.telemetria.conf}<Delta tone="good">{seed.telemetria.delta}</Delta></>} label="propuestas confirmadas" />
                    <StatCell n={seed.telemetria.skills} label="skills disparados" />
                  </StatGrid>
                  <div style={{ padding: "var(--sk-sp-2) 0 var(--sk-sp-2)" }}>
                    {seed.telemetria.bars.map((b) => <BarRow key={b.k} k={b.k} width={b.width} v={b.v} />)}
                  </div>
                </>
              ) : (
                <CardPad><Empty inline title="Aún no medimos esta casa.">Mensajes, confirmaciones y skills disparados llegarán cuando los contemos — antes que un número inventado, ninguno.</Empty></CardPad>
              )}
            </Card>

            <Card flush aria-label="Rastro de la memoria">
              <CardHeader title="Rastro" hint={seed.rastro ? "solo añade — nunca reescribe · rastro de la fase dev" : "solo añade — nunca reescribe"} />
              {seed.rastro ? (
                <Feed>
                  {seed.rastro.map((r, i) => (
                    <FeedItem key={i} t={r.t}><FeedWho>{r.who}</FeedWho> {r.resto}{r.opaco && <Opaque>{r.opaco}</Opaque>}</FeedItem>
                  ))}
                </Feed>
              ) : (
                // un apunte inventado dentro de un registro que promete «solo
                // añade» sería justo lo contrario de lo que la tarjeta dice
                <CardPad><Empty inline title="El rastro fino aún no se guarda.">Hoy la casa tiene {criterios.length} criterios y {m.decisiones.length} decisiones; quién tocó qué y cuándo se registrará cuando el rastro tenga su propia lectura.</Empty></CardPad>
              )}
            </Card>
          </div>
        </div>
      </Stale>
    </>
  );
}
